import { useState, useRef } from "react";
import { ChevronDown, Globe, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

const CommunityMegaMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 200);
  };

  const handleNavigate = (href: string) => {
    navigate(href);
    setIsOpen(false);
  };

  return (
    <div className="relative hidden md:block" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <button className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-foreground hover:text-foreground/80 transition-colors rounded-full hover:bg-muted/50 outline-none">
        Community
        <ChevronDown className={`h-3 w-3 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {/* Mega Menu */}
      <div className={`absolute top-full right-0 pt-3 z-50 transition-all duration-300 ${isOpen ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-3 pointer-events-none"}`}>
        <div className="bg-card border border-border rounded-2xl shadow-2xl w-[480px] p-5 backdrop-blur-xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-border/50">
            <h3 className="text-sm font-semibold text-foreground">Community</h3>
            <span className="text-xs text-muted-foreground">Join the movement</span>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* About Us Card */}
            <button 
              onClick={() => handleNavigate("/about")} 
              className="group col-span-2 p-4 rounded-xl border border-border/50 bg-gradient-to-br from-foreground/5 via-muted/30 to-foreground/5 hover:from-foreground/10 hover:to-foreground/10 transition-all duration-300 hover:border-muted-foreground/30 text-left" 
              style={{ animation: isOpen ? 'dropdownItemIn 0.4s ease-out 0ms both' : 'none' }}
            >
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-foreground/10 transition-all duration-300 group-hover:bg-foreground/20 group-hover:scale-110">
                  <Globe className="h-5 w-5 text-foreground" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold text-foreground group-hover:text-foreground/80 transition-colors">
                      About Us
                    </span>
                    <ArrowRight className="h-3 w-3 text-muted-foreground transition-transform duration-300 group-hover:translate-x-1 group-hover:text-foreground" />
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed mb-3">
                    Learn about our mission to empower Africa through decentralized physical infrastructure networks.
                  </p>
                </div>
              </div>
            </button>
          </div>

          {/* Social Links */}
          <div 
            className="mt-4 p-3 rounded-xl bg-muted/30 border border-border/50" 
            style={{ animation: isOpen ? 'dropdownItemIn 0.4s ease-out 200ms both' : 'none' }}
          >
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-3">Connect with us</p>
            <div className="flex items-center gap-2">
              <a 
                href="https://x.com/Depinland_org" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-card hover:bg-foreground/10 transition-colors border border-border/50"
              >
                {/* X (Twitter) Icon */}
                <svg className="h-3.5 w-3.5 text-foreground" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
                <span className="text-xs text-foreground">Twitter</span>
              </a>
              <a 
                href="https://discord.gg/paEy3nMcuH" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-card hover:bg-foreground/10 transition-colors border border-border/50"
              >
                {/* Discord Icon */}
                <svg className="h-3.5 w-3.5 text-foreground" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189z" />
                </svg>
                <span className="text-xs text-foreground">Discord</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommunityMegaMenu;
